import { useState } from 'react';
import { useDatabase } from '../../hooks/useDatabase';
import { usePendingChanges } from '../../hooks/usePendingChanges';
import { Button, Spinner, Tooltip } from 'flowbite-react';
import { FiRotateCcw, FiSave, FiRefreshCw } from 'react-icons/fi';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../hooks/useAuth';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import ChangesTable, { Column } from '../../components/changes/ChangesTable';

const PendingChangesPage = () => {
  const { workspaceId } = useDatabase();
  const { pendingChanges, loading, savePendingChanges, undoPendingChange, undoAllPendingChanges } = usePendingChanges();
  const online = useOnlineStatus();
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { token, client } = useAuth();

  const handleSave = async () => {
    if (!workspaceId || !online || pendingChanges.length === 0) return;
    try {
      setSaving(true);
      if (!token) {
        enqueueSnackbar('Not signed in: no authentication token available', { variant: 'error' });
        return;
      }
      await savePendingChanges(client, workspaceId, token);
      enqueueSnackbar('Changes saved successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error saving changes:', error);
      enqueueSnackbar(`Error saving changes: ${error}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formatCommandType = (command: any) => {
    if (!command || !command.type) return 'Unknown';
    return command.type;
  };

  const formatEvents = (events: any[]) => {
    if (!events || !Array.isArray(events) || events.length === 0) return 'No events';
    return `${events.length} event${events.length > 1 ? 's' : ''}`;
  };

  const handleUndo = async (index: number) => {
    try {
      await undoPendingChange(index);
      enqueueSnackbar('Change undone successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error undoing change:', error);
      enqueueSnackbar(`Error undoing change: ${error}`, { variant: 'error' });
    }
  };

  const handleReset = async () => {
    if (pendingChanges.length === 0) return;
    try {
      setResetting(true);
      await undoAllPendingChanges();
      enqueueSnackbar('All changes reset successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error resetting changes:', error);
      enqueueSnackbar(`Error resetting changes: ${error}`, { variant: 'error' });
    } finally {
      setResetting(false);
    }
  };

  const columns: Column<(typeof pendingChanges)[number]>[] = [
    {
      key: 'commandType',
      header: 'Command Type',
      render: (change) => (
        <span className="whitespace-nowrap font-mono font-medium text-cm-text-primary text-xs">
          {formatCommandType(change.command)}
        </span>
      ),
    },
    {
      key: 'entityId',
      header: 'Entity ID',
      render: (change) => (
        <span className="font-mono text-xs text-cm-text-secondary">
          {change.command.type === 'DeleteItem' && change.command.id ? (
            typeof change.command.id === 'object'
              ? `${change.command.id.type_}: ${change.command.id.id}`
              : String(change.command.id)
          ) : change.command.type === 'CreateItem' && change.command.id ? (
            String(change.command.id)
          ) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'resultingEvents',
      header: 'Events',
      render: (change) => <span className="text-xs text-cm-text-muted">{formatEvents(change.resultingEvents)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (_change, index) => (
        <div className="text-right">
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-cm-text-muted hover:text-cm-accent-orange flex items-center gap-1"
            onClick={() => handleUndo(index)}
          >
            <FiRotateCcw className="w-3 h-3" />
            Undo
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-semibold text-cm-text-primary">Pending Changes</h1>
          <p className="mt-1 text-sm text-cm-text-secondary">
            {pendingChanges.length} unsaved change{pendingChanges.length !== 1 ? 's' : ''}
            {!online && <span className="text-cm-accent-gold ml-2">(offline)</span>}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            disabled={resetting || pendingChanges.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cm-border-subtle text-sm text-cm-text-muted hover:text-cm-text-primary hover:border-cm-border-mid transition-colors disabled:opacity-40"
          >
            {resetting ? (
              <div className="w-3.5 h-3.5 border-2 border-cm-text-muted border-t-transparent rounded-full animate-spin" />
            ) : (
              <FiRefreshCw className="w-3.5 h-3.5" />
            )}
            Reset All
          </button>

          <button
            onClick={handleSave}
            disabled={saving || !online || pendingChanges.length === 0}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-cm-accent-orange text-cm-bg-deep text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            {saving ? (
              <div className="w-3.5 h-3.5 border-2 border-cm-bg-deep border-t-transparent rounded-full animate-spin" />
            ) : (
              <FiSave className="w-3.5 h-3.5" />
            )}
            Save Changes
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 justify-center p-12 text-cm-text-muted text-sm">
          <div className="w-5 h-5 border-2 border-cm-accent-orange border-t-transparent rounded-full animate-spin" />
          Loading...
        </div>
      ) : (
        <ChangesTable
          columns={columns}
          rows={pendingChanges}
          emptyMessage="No pending changes. All changes have been saved."
        />
      )}
    </div>
  );
};

export default PendingChangesPage;
